import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const TEAL = '#00798E';

/** sx to merge onto the currently-highlighted <TableRow>. */
export const ACTIVE_ROW_SX = {
  bgcolor: 'rgba(0,121,142,0.10)',
  boxShadow: `inset 3px 0 0 ${TEAL}`,
  outline: 'none',
};

/**
 * Keyboard navigation for a "search box + results table" list screen
 * (Tally-style). Additive — existing row onClick handlers keep working.
 *
 *   const kb = useListKeyboard({ rowCount: rows.length, onOpen: (i) => go(rows[i]) });
 *   <TextField {...kb.searchProps} value={search} onChange={...} />
 *   {rows.map((row, idx) => (
 *     <TableRow key={row.id} hover {...kb.getRowProps(idx)}
 *       sx={{ ...base, ...(kb.activeIdx === idx ? ACTIVE_ROW_SX : {}) }}>
 *   ))}
 *
 * Behaviour:
 *   - on mount → focuses the search input
 *   - '/' (when not typing in a field) → focus + select the search box
 *   - Alt+N (anywhere) → onCreate(), if provided — opens the "New …" flow
 *   - ArrowDown / ArrowUp from the search box or a row → move the highlight
 *   - ArrowUp on the first row → back to the search box
 *   - Enter in the search box → open the highlighted row (or the first row)
 *   - Enter on a row → open that row
 *   - Home / End → first / last row
 *   - highlight resets whenever `rowCount` (or `resetKey`) changes
 */
export function useListKeyboard({ rowCount = 0, onOpen, onCreate, enabled = true, resetKey }) {
  const [activeIdx, setActiveIdx] = useState(-1);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onCreateRef = useRef(onCreate);
  onCreateRef.current = onCreate;

  rowRefs.current.length = rowCount;

  useEffect(() => { setActiveIdx(-1); }, [rowCount, resetKey]);

  useEffect(() => {
    if (enabled) searchRef.current?.focus();
  }, [enabled]);

  const focusRow = useCallback((idx) => {
    const el = rowRefs.current[idx];
    if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); }
  }, []);

  const move = useCallback((delta) => {
    setActiveIdx((cur) => {
      if (rowCount === 0) return -1;
      let next = cur < 0 ? (delta > 0 ? 0 : rowCount - 1) : cur + delta;
      next = Math.max(0, Math.min(rowCount - 1, next));
      setTimeout(() => focusRow(next), 0);
      return next;
    });
  }, [rowCount, focusRow]);

  const open = useCallback((idx) => {
    if (idx >= 0 && idx < rowCount) onOpenRef.current?.(idx);
  }, [rowCount]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select?.();
      } else if (e.altKey && (e.key === 'n' || e.key === 'N') && onCreateRef.current) {
        e.preventDefault();
        onCreateRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  const searchKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      open(activeIdx >= 0 ? activeIdx : (rowCount > 0 ? 0 : -1));
    }
  }, [move, open, activeIdx, rowCount]);

  const rowKeyDown = useCallback((idx) => (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx === 0) { setActiveIdx(-1); searchRef.current?.focus(); }
      else move(-1);
    }
    else if (e.key === 'Enter') { e.preventDefault(); open(idx); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveIdx(0); setTimeout(() => focusRow(0), 0); }
    else if (e.key === 'End') {
      e.preventDefault();
      const last = rowCount - 1;
      setActiveIdx(last);
      setTimeout(() => focusRow(last), 0);
    }
  }, [move, open, focusRow, rowCount]);

  const getRowProps = useCallback((idx) => ({
    ref: (el) => { rowRefs.current[idx] = el; },
    tabIndex: -1,
    onKeyDown: rowKeyDown(idx),
    onClick: () => setActiveIdx(idx),
    'data-active': activeIdx === idx ? 'true' : undefined,
  }), [rowKeyDown, activeIdx]);

  const searchProps = useMemo(() => ({
    inputRef: searchRef,
    onKeyDown: searchKeyDown,
  }), [searchKeyDown]);

  return {
    activeIdx,
    setActiveIdx,
    getRowProps,
    searchProps,
    focusSearch: () => searchRef.current?.focus(),
  };
}
